import { action, makeObservable, observable } from 'mobx';

import { FALLBACK_COUNTRY_CODE } from '../../features/geography/constants';
import type { Country, Territory } from '../../features/geography/types';
import { getRequest } from '../../services/api';
import type { ApiResponse } from '../../services/types';
import type { RootStore } from '../RootStore';

import type { FetchCountriesOptionsType, InitRegionResourcesOptionsType } from './types';

export class RegionStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public territories: Territory[] = [];

  @action
  private setTerritories(territories: Territory[]) {
    this.territories = territories;
  }

  @observable
  public countries: Country[] = [];

  @action
  private setCountries(countries: Country[]) {
    this.countries = countries;
  }

  public async initTerritories(options?: InitRegionResourcesOptionsType) {
    if (!this.territories.length || options?.overrideData) {
      const countryCode = this.rootStore.liveGeoStore.currentGeo?.countryCode || FALLBACK_COUNTRY_CODE;
      const territories = await this.fetchTerritoriesByCountryCode({ countryCode, excludeInactive: false });
      if (territories.length) {
        this.setTerritories(territories);
      }
    }
  }

  public async initCountries(options?: InitRegionResourcesOptionsType) {
    if (!this.countries.length || options?.overrideData) {
      const countries = await this.fetchCountries();
      if (countries.length) {
        this.setCountries(countries);
      }
    }
  }

  private async fetchCountries(options?: FetchCountriesOptionsType) {
    try {
      const response = await getRequest<ApiResponse<Country[]>>('/v2/geography/countries', options || {});
      if (!response.data || !response.data.data) {
        return [];
      }
      return response.data.data || [];
    } catch (e) {
      return [];
    }
  }

  private async fetchTerritoriesByCountryCode(options: {
    countryCode?: string;
    excludeInactive?: boolean;
  }): Promise<Territory[]> {
    try {
      const response = await getRequest<ApiResponse<Territory[]>>('geography/territories', options);
      if (!response.data || !response.data.data) {
        return [];
      }
      return response.data.data || [];
    } catch (error) {
      return [];
    }
  }
}
